import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatExpanded, setMobileCatExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2.5 group cursor-pointer"
          >
            <span className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
              <i className="ri-shopping-bag-3-line text-background-50 text-sm"></i>
            </span>
            <span className="text-lg font-bold text-foreground-900 font-heading tracking-tight whitespace-nowrap">
              Kakobuy<span className="text-accent-600">Finds</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('nav_home')}
            </Link>

            <Link
              to="/poradnik-zakupowy"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Poradnik zakupowy
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {t('nav_categories')}
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 w-64 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto animate-fade-in">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-800 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                      <span className="text-foreground-400 text-xs ml-2">{cat.nameEn}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/kakobuy-spreadsheet"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('nav_guide')}
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold px-5 py-2 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('hero_btn_primary')}
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl text-foreground-800`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-fade-in shadow-lg max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="px-6 py-4 flex flex-col gap-3">
            <Link
              to="/"
              className="flex items-center gap-3 text-sm font-medium text-foreground-700 py-2.5 px-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-home-4-line text-accent-500 text-lg"></i>
              {t('nav_home')}
            </Link>

            <Link
              to="/poradnik-zakupowy"
              className="flex items-center gap-3 text-sm font-medium text-foreground-700 py-2.5 px-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-compass-3-line text-accent-500 text-lg"></i>
              Poradnik zakupowy
            </Link>

            <div className="py-1">
              <button
                onClick={() => setMobileCatExpanded(!mobileCatExpanded)}
                className="flex items-center justify-between w-full text-sm font-semibold text-foreground-700 py-2.5 px-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-grid-line text-accent-500 text-lg"></i>
                  {t('nav_categories')}
                </span>
                <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 ${mobileCatExpanded ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileCatExpanded && (
                <div className="mt-2 ml-4 grid grid-cols-2 gap-1 max-h-60 overflow-y-auto bg-background-100 rounded-xl p-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 text-sm text-foreground-600 hover:text-accent-700 py-2 px-2 rounded-lg hover:bg-background-50 transition-colors cursor-pointer"
                    >
                      <span className="w-7 h-7 rounded-md bg-accent-100/60 flex items-center justify-center flex-shrink-0">
                        <i className={`ri-${cat.id === 911 ? 'footprint' : cat.id === 912 ? 't-shirt' : cat.id === 913 ? 't-shirt-air' : cat.id === 914 ? 'hanger' : cat.id === 915 ? 'coupon' : cat.id === 916 ? 'sun' : cat.id === 917 ? 'folder' : cat.id === 918 ? 'blur' : cat.id === 919 ? 'shirt' : cat.id === 920 ? 'glasses' : cat.id === 921 ? 'handbag' : cat.id === 922 ? 'loop-left' : cat.id === 923 ? 'drop' : 'more'}-line text-accent-600 text-xs`}></i>
                      </span>
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/kakobuy-spreadsheet"
              className="flex items-center gap-3 text-sm font-medium text-foreground-700 py-2.5 px-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-book-read-line text-accent-500 text-lg"></i>
              {t('nav_guide')}
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 text-sm font-semibold px-5 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 cursor-pointer mt-2"
            >
              <i className="ri-shopping-bag-3-line"></i>
              {t('hero_btn_primary')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}